'use client';

import InlineStyle from '@/components/InlineStyle';
import { motion } from 'framer-motion';
import { Activity, BarChart3, Clock, MessageSquareWarning, Radar, ShieldCheck, Sparkles, Target, TrendingUp, Zap } from 'lucide-react';
import { Button, Card, CheckList, FaqAccordion, PageHero, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import BookDemoSection from '@/components/BookDemoSection';
import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';

// AUTO-GENERATED by the on-brand page generator. Real components only.
const ICONS = [Radar, Clock, Activity, MessageSquareWarning, TrendingUp, ShieldCheck, Sparkles, Target, BarChart3, Zap];
const METRICS = [{"value":"25%","label":"Increase in CSAT"},{"value":"30","label":"Minutes Average Response Time"},{"value":"90%","label":"Automated Query Resolution"}];
const PROBLEMS = ["Inconsistent brand voice and tone across channels","Difficulty in tracking and responding to customer queries in real-time","Inability to measure and improve customer satisfaction effectively"];
const CAPABILITIES = [{"title":"Social Media Monitoring","body":"Track and respond to customer queries in real-time, across multiple social media platforms"},{"title":"AI-Powered Chatbots","body":"Automate query resolution with intelligent chatbots that understand customer intent and preferences"},{"title":"Unified Customer Profile","body":"Get a single, unified view of customer interactions and preferences across channels"},{"title":"Real-Time Analytics","body":"Measure and improve customer satisfaction with real-time analytics and insights"}];
const FAQS = [{"q":"How does your platform integrate with our existing systems?","a":"Our platform integrates seamlessly with your existing systems, including CRM, ERP, and helpdesk software"},{"q":"Can I customize the chatbot to fit my brand's voice and tone?","a":"Yes, our chatbot can be customized to fit your brand's voice and tone, ensuring consistency across channels"},{"q":"How do you ensure data security and compliance?","a":"We ensure data security and compliance with industry-standard encryption, access controls, and regular audits"},{"q":"What kind of support and training do you offer?","a":"We offer comprehensive support and training, including onboarding, workshops, and ongoing customer success management"}];

export default function GeneratedBody() {
  return (
    <div>
      <span className="sr-only">{"Improve CSAT with AI-powered social customer care, unifying channels for seamless retail experiences. Discover how."}</span>

      {/* 1 — Hero */}
      <PageHero
        align="center"
        eyebrow={"Elevate Your Customer Experience"}
        title={<>{"Transform Your "}<span className="serif-accent italic">{"Customer"}</span>{"Care"}</>}
        subtitle={"Deliver exceptional social customer care across channels, with a unified platform that boosts CSAT and drives loyalty"}
        actions={<Button variant="cta" size="lg" href="#book-demo">{"Get Started Today"}</Button>}
      />

      {/* 2 — Proof metrics */}
      <MetricsBand items={METRICS} columns={3} />

      {/* 3 — Problem / solution */}
      <PageSection bg="tint">
        <div className="lp-split">
          <SectionTitle
            align="left"
            eyebrow={"The Challenge of Social Customer Care"}
            title={<>{"Managing Multiple "}<span className="serif-accent italic">{"Channels"}</span>{"Effectively"}</>}
            subtitle={"Retail brands face numerous challenges in delivering consistent, high-quality social customer care across various channels"}
          />
          <Card padding="xl" radius="2xl">
            <CheckList items={PROBLEMS} />
          </Card>
        </div>
      </PageSection>

      {/* 4 — Capability cards */}
      <PageSection bg="page">
        <SectionTitle
          align="center"
          eyebrow={"Unified Social Customer Care Capabilities"}
          title={<>{"Unlock the Power of "}<span className="serif-accent italic">{"Omnichannel"}</span>{"Engagement"}</>}
          subtitle={"Our platform empowers retail brands to deliver seamless, personalized customer experiences across social channels"}
        />
        <motion.div className="lp-grid" variants={staggerContainer(0.08)} initial="hidden" whileInView="show" viewport={viewportOnce}>
          {CAPABILITIES.map((cap, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div key={cap.title} variants={fadeUp}>
                <Card padding="xl" radius="2xl" hover className="lp-card">
                  <span className="lp-icon" aria-hidden="true">
                    <Icon size={22} color="var(--brand)" strokeWidth={2.2} />
                  </span>
                  <h3 className="lp-card-title">{cap.title}</h3>
                  <p className="lp-card-body">{cap.body}</p>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </PageSection>

      {/* 5 — FAQ */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle align="center" title={<>{"Frequently Asked "}<span className="serif-accent italic">{"Questions"}</span>{""}</>} />
        <FaqAccordion items={FAQS} columns={2} name="generated-faq" />
      </PageSection>

      {/* 6 — Book a demo (always last) */}
      <BookDemoSection />

      <InlineStyle css={`
        .lp-split { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
        .lp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .lp-card { height: 100%; }
        .lp-icon { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: var(--radius-lg); background: var(--brand-subtle); border: 1px solid var(--brand-border); margin-bottom: 16px; }
        .lp-card-title { font-size: 19px; font-weight: 700; color: var(--text-heading, #0D1B3E); margin: 0 0 8px; letter-spacing: -0.01em; }
        .lp-card-body { font-size: 14.5px; line-height: 1.65; color: var(--text-body); margin: 0; }
        @media (max-width: 900px) { .lp-split { grid-template-columns: 1fr; gap: 32px; } .lp-grid { grid-template-columns: 1fr; } }
      `} />
    </div>
  );
}
